    <script setup>
import { ref, watch, onMounted } from 'vue';
import InputSwitch from 'primevue/inputswitch';
import Calendar from 'primevue/calendar';
import Button from 'primevue/button';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: 'Arbeitszeiten'
    },
    errors: {
        type: Object,
        default: () => ({})
    },
    companyHours: {
        type: Array,
        default: null
    },
    showCustomerHours: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'clear-error']);

const days = [
    { label: 'Montag', value: 1 },
    { label: 'Dienstag', value: 2 },
    { label: 'Mittwoch', value: 3 },
    { label: 'Donnerstag', value: 4 },
    { label: 'Freitag', value: 5 },
    { label: 'Samstag', value: 6 },
    { label: 'Sonntag', value: 0 },
];

const localHours = ref(days.map(day => ({
    day_of_week: day.value,
    label: day.label,
    is_workday: day.value !== 0 && day.value !== 6,
    start_time: null,
    end_time: null,
    customer_start_time: null,
    customer_end_time: null,
    id: null
})));

const parseTime = (timeStr) => {
    if (!timeStr) return null;
    if (timeStr instanceof Date) return timeStr;
    const parts = String(timeStr).split(':');
    if (parts.length < 2) return null;
    const date = new Date();
    date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
    return date;
};

const formatTime = (date) => {
    if (!date) return null;
    if (!(date instanceof Date)) return String(date).substring(0, 5);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const normalizeTime = (time) => {
    if (!time) return null;
    if (time instanceof Date) return formatTime(time);
    return String(time).substring(0, 5);
};

const sync = () => {
    const hoursToEmit = localHours.value.map(h => ({
        day_of_week: h.day_of_week,
        is_workday: h.is_workday,
        start_time: formatTime(h.start_time),
        end_time: formatTime(h.end_time),
        customer_start_time: props.showCustomerHours ? formatTime(h.customer_start_time) : undefined,
        customer_end_time: props.showCustomerHours ? formatTime(h.customer_end_time) : undefined,
        id: h.id
    }));
    emit('update:modelValue', hoursToEmit);
};

const updateFromProps = (newVal) => {
    if (!newVal || !Array.isArray(newVal)) return;

    localHours.value.forEach(local => {
        const provided = newVal.find(h => Number(h.day_of_week) === Number(local.day_of_week));
        if (!provided) return;

        // Guard against unnecessary updates that could trigger loops
        if (
            local.is_workday === !!provided.is_workday &&
            normalizeTime(local.start_time) === normalizeTime(provided.start_time) &&
            normalizeTime(local.end_time) === normalizeTime(provided.end_time) &&
            (!props.showCustomerHours || (
                normalizeTime(local.customer_start_time) === normalizeTime(provided.customer_start_time) &&
                normalizeTime(local.customer_end_time) === normalizeTime(provided.customer_end_time)
            ))
        ) {
            if (provided.id && !local.id) local.id = provided.id;
            return;
        }

        local.is_workday = !!provided.is_workday;
        local.start_time = provided.start_time ? parseTime(provided.start_time) : null;
        local.end_time = provided.end_time ? parseTime(provided.end_time) : null;
        if (props.showCustomerHours) {
            local.customer_start_time = provided.customer_start_time ? parseTime(provided.customer_start_time) : null;
            local.customer_end_time = provided.customer_end_time ? parseTime(provided.customer_end_time) : null;
        }
        if (provided.id) local.id = provided.id;
    });
};

onMounted(() => {
    updateFromProps(props.modelValue);
});

watch(() => props.modelValue, (newVal) => {
    updateFromProps(newVal);
}, { deep: true });

const isCompanyClosed = (dayOfWeek) => {
    if (!props.companyHours) return false;
    const companyDay = props.companyHours.find(h => Number(h.day_of_week) === Number(dayOfWeek));
    return companyDay && !companyDay.is_workday;
};

watch(() => props.companyHours, (newHours) => {
    if (newHours && Array.isArray(localHours.value)) {
        let changed = false;
        localHours.value.forEach(h => {
            const companyDay = newHours.find(ch => Number(ch.day_of_week) === Number(h.day_of_week));
            if (companyDay && !companyDay.is_workday && h.is_workday) {
                h.is_workday = false;
                h.start_time = null;
                h.end_time = null;
                h.customer_start_time = null;
                h.customer_end_time = null;
                changed = true;
            }
        });
        if (changed) sync();
    }
}, { deep: true, immediate: true });

const copyMondayToAll = () => {
    const monday = localHours.value.find(h => h.day_of_week === 1);
    if (!monday) return;

    localHours.value.forEach(h => {
        if (h.day_of_week !== 0 && h.day_of_week !== 6) {
            h.is_workday = monday.is_workday;
            h.start_time = monday.start_time ? new Date(monday.start_time) : null;
            h.end_time = monday.end_time ? new Date(monday.end_time) : null;
            h.customer_start_time = monday.customer_start_time ? new Date(monday.customer_start_time) : null;
            h.customer_end_time = monday.customer_end_time ? new Date(monday.customer_end_time) : null;
        }
    });
    sync();
};

const getCompanyDay = (dayValue) => {
    if (!props.companyHours) return null;
    return props.companyHours.find(h => Number(h.day_of_week) === Number(dayValue));
};

// Internal sync on any change
watch(localHours, () => {
    sync();
}, { deep: true });

</script>

<template>
    <div class="scheduler-container bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div class="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
            <h3 class="text-lg font-bold text-gray-800">{{ title }}</h3>
            <Button 
                type="button" 
                @click="copyMondayToAll"
                icon="pi pi-copy" 
                label="Montag auf alle Wochentage kopieren" 
                class="p-button-text p-button-sm !text-gray-500 !p-0 hover:!text-black"
            />
        </div>

        <div class="overflow-x-auto">
            <table class="w-full text-left border-collapse">
                <thead>
                    <tr class="text-xs font-bold text-gray-400 uppercase tracking-wider">
                        <th class="px-6 py-4">Wochentag</th>
                        <th class="px-6 py-4">Status</th>
                        <th class="px-6 py-4">Beginn</th>
                        <th class="px-6 py-4">Ende</th>
                        <template v-if="showCustomerHours">
                            <th class="px-6 py-4">Kunden-Beginn</th>
                            <th class="px-6 py-4">Kunden-Ende</th>
                        </template>
                        <th class="px-6 py-4" v-if="companyHours">Unternehmenszeit</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-50">
                    <tr v-for="(hour, index) in localHours" :key="hour.day_of_week" 
                        class="hover:bg-gray-50/50 transition-colors"
                        :class="{'bg-red-50/10': !hour.is_workday}">
                        
                        <td class="px-6 py-4 font-bold text-gray-700">
                            {{ hour.label }}
                        </td>

                        <td class="px-6 py-4">
                            <div class="flex items-center gap-3">
                                <InputSwitch 
                                    v-model="hour.is_workday" 
                                    :disabled="isCompanyClosed(hour.day_of_week)"
                                    @change="emit('clear-error', `working_hours.${index}.start_time`); emit('clear-error', `working_hours.${index}.end_time`)" 
                                />
                                <span class="text-xs font-medium uppercase tracking-tight" 
                                      :class="hour.is_workday ? 'text-green-600' : 'text-gray-400'">
                                    {{ hour.is_workday ? 'Geöffnet' : 'Geschlossen' }}
                                </span>
                            </div>
                        </td>

                        <td class="px-6 py-4">
                            <div v-if="hour.is_workday">
                                <Calendar 
                                    v-model="hour.start_time" 
                                    timeOnly 
                                    :stepMinute="15"
                                    hourFormat="24"
                                    placeholder="Beginn"
                                    showIcon
                                    iconDisplay="input"
                                    icon="pi pi-clock"
                                    :class="{'p-invalid': errors[`working_hours.${index}.start_time`]}"
                                    @update:model-value="() => {}"
                                    inputClass="w-24 text-sm font-medium border-gray-200 p-2"
                                    :minDate="getCompanyDay(hour.day_of_week)?.start_time ? parseTime(getCompanyDay(hour.day_of_week).start_time) : null"
                                />
                                <small v-if="errors[`working_hours.${index}.start_time`]" class="p-error block mt-1 text-[10px]">{{ errors[`working_hours.${index}.start_time`] }}</small>
                            </div>
                            <span v-else class="text-gray-300 text-sm">—</span>
                        </td>

                        <td class="px-6 py-4">
                            <div v-if="hour.is_workday">
                                <Calendar 
                                    v-model="hour.end_time" 
                                    timeOnly 
                                    :stepMinute="15"
                                    hourFormat="24"
                                    placeholder="Ende"
                                    showIcon
                                    iconDisplay="input"
                                    icon="pi pi-clock"
                                    :class="{'p-invalid': errors[`working_hours.${index}.end_time`]}"
                                    @update:model-value="() => {}"
                                    inputClass="w-24 text-sm font-medium border-gray-200 p-2"
                                    :maxDate="getCompanyDay(hour.day_of_week)?.end_time ? parseTime(getCompanyDay(hour.day_of_week).end_time) : null"
                                />
                                <small v-if="errors[`working_hours.${index}.end_time`]" class="p-error block mt-1 text-[10px]">{{ errors[`working_hours.${index}.end_time`] }}</small>
                            </div>
                            <span v-else class="text-gray-300 text-sm">—</span>
                        </td>

                        <template v-if="showCustomerHours">
                            <td class="px-6 py-4">
                                <div v-if="hour.is_workday">
                                    <Calendar 
                                        v-model="hour.customer_start_time" 
                                        timeOnly 
                                        :stepMinute="15"
                                        hourFormat="24"
                                        placeholder="Kunden-Beginn"
                                        showIcon
                                        iconDisplay="input"
                                        icon="pi pi-clock"
                                        :class="{'p-invalid': errors[`working_hours.${index}.customer_start_time`]}"
                                        @update:model-value="() => {}"
                                        inputClass="w-24 text-sm font-medium border-gray-200 p-2"
                                    />
                                    <small v-if="errors[`working_hours.${index}.customer_start_time`]" class="p-error block mt-1 text-[10px]">{{ errors[`working_hours.${index}.customer_start_time`] }}</small>
                                </div>
                                <span v-else class="text-gray-300 text-sm">—</span>
                            </td>

                            <td class="px-6 py-4">
                                <div v-if="hour.is_workday">
                                    <Calendar 
                                        v-model="hour.customer_end_time" 
                                        timeOnly 
                                        :stepMinute="15"
                                        hourFormat="24"
                                        placeholder="Kunden-Ende"
                                        showIcon
                                        iconDisplay="input"
                                        icon="pi pi-clock"
                                        :class="{'p-invalid': errors[`working_hours.${index}.customer_end_time`]}"
                                        @update:model-value="() => {}"
                                        inputClass="w-24 text-sm font-medium border-gray-200 p-2"
                                    />
                                    <small v-if="errors[`working_hours.${index}.customer_end_time`]" class="p-error block mt-1 text-[10px]">{{ errors[`working_hours.${index}.customer_end_time`] }}</small>
                                </div>
                                <span v-else class="text-gray-300 text-sm">—</span>
                            </td>
                        </template>

                        <td class="px-6 py-4" v-if="companyHours">
                            <div class="flex flex-col gap-1">
                                <span v-if="getCompanyDay(hour.day_of_week)" class="text-[10px] text-gray-500 font-medium">
                                    {{ getCompanyDay(hour.day_of_week).is_workday ? 
                                        `${getCompanyDay(hour.day_of_week).start_time.substring(0,5)} - ${getCompanyDay(hour.day_of_week).end_time.substring(0,5)}` : 
                                        'Geschlossen' }}
                                </span>
                                <div v-if="!getCompanyDay(hour.day_of_week)?.is_workday" class="py-1 px-2 bg-red-100 text-red-600 rounded text-[10px] font-bold text-center uppercase tracking-tighter">
                                    Geschlossen
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
:deep(.p-inputswitch.p-inputswitch-checked .p-inputswitch-slider) {
    background: #000;
}
:deep(.p-calendar .p-inputtext) {
    border-radius: 8px;
}
:deep(.p-calendar:not(.p-calendar-disabled).p-focus > .p-inputtext) {
    border-color: #000;
    box-shadow: none;
}
</style>
